/**
 * Painted Guardian art (public/ paths), by hero id. Anything missing falls back to the code-drawn
 * figure or the letter card. Strips use 512px cells with the feet at (256, 490) (docs/DESIGN.md §4.3):
 * idle 4 frames, attack 8 frames starting and ending on idle frame 1. Volley FX
 * (`fx_guardian_<id>_shot.png` 128x128, spun in flight; `fx_guardian_<id>_impact.png` 4 cells of 256x256)
 * replace the code-drawn `shot` when present.
 */
export interface GuardianArt {
  idle?: string;
  attack?: string;
  portrait?: string;
  /** Signature volley drawn by the renderer; without one, melee Guardians throw a slash, others an orb. */
  shot?: GuardianShot;
  /** Painted projectile and impact burst for the volley. */
  shotImage?: string;
  impactImage?: string;
  /** Tumble the painted projectile in flight (a thrown brick); otherwise it points along its path (arrows, fireballs). */
  shotSpin?: boolean;
  /** Attack strips that aren't the standard 8 frames at 16 fps (e.g. a rigged animation), and the frame the volley leaves on. */
  attackFrames?: number;
  attackFps?: number;
  strikeFrame?: number;
  /** Attack cell width when the strip carries its effect beside the body (768 = body + FX, same top-left origin). */
  attackCell?: number;
  /** Idle strips longer than the standard 4 frames (e.g. Globe Guardian's slow 32-frame spin). */
  idleFrames?: number;
}

/** Block Knight hurls a spinning toy brick that shatters into chips. */
export type GuardianShot = 'brick';

/** The standard files for a Guardian id (hero_<id>_idle / _attack / _portrait), with overrides. */
function art(id: string, over: Partial<GuardianArt> = {}): GuardianArt {
  return { idle: `art/hero_${id}_idle.png`, attack: `art/hero_${id}_attack.png`, portrait: `art/hero_${id}_portrait.png`, ...over };
}

export const GUARDIAN_ART: Readonly<Record<string, GuardianArt>> = {
  knight: { idle: 'art/hero_knight_idle.png', attack: 'art/hero_knight_attack.png', portrait: 'art/hero_knight_portrait.png', shot: 'brick',
    shotImage: 'art/fx_guardian_knight_shot.png', impactImage: 'art/fx_guardian_knight_impact.png', shotSpin: true },
  // Batch 2 (handoff/art-inbox/batch2): the approved revisions are the _v2 files.
  archer: art('archer'),
  mage: art('mage', { idle: 'art/hero_mage_idle_v2.png' }),
  rogue: art('rogue', { attack: 'art/hero_rogue_attack_v2.png' }),
  cleric: art('cleric'),
  paladin: art('paladin', { attack: 'art/hero_paladin_attack_v2.png', shotImage: 'art/fx_guardian_paladin_shot.png' }),
  ranger: art('ranger', { shotImage: 'art/fx_guardian_ranger_shot.png' }),
  warlock: art('warlock'),
  dragoon: art('dragoon'),
  titan: art('titan'),
  // Batch 3 (handoff/art-inbox/batch3, see INTEGRATED.md): Guardians #11-50: the full roster is painted. Approved alternates were
  // copied in under the standard names; Hammer Tom's attack is a 25-frame rig (authored at 24 fps).
  frostmage: art('frostmage', { shotImage: 'art/fx_guardian_frostmage_shot.png', shotSpin: true }),
  berserker: art('berserker', { attackFrames: 25, attackFps: 26, strikeFrame: 12 }), // ~1s, so it fits the once-a-second volley
  gunner: art('gunner'),
  druid: art('druid'),
  monk: art('monk'),
  hunter: art('hunter'),
  necro: art('necro'),
  samurai: art('samurai'),
  sniper: art('sniper'),
  storm: art('storm'),
  templar: art('templar'),
  corsair: art('corsair'),
  pyro: art('pyro'),
  golem: art('golem'),
  windrider: art('windrider'),
  oracle: art('oracle'),
  warlord: art('warlord'),
  starbow: art('starbow'),
  lich: art('lich'),
  phoenix: art('phoenix'),
  voidhunter: art('voidhunter', { attackFrames: 32, attackFps: 50, strikeFrame: 14 }), // v3 rig throw, played at 200% as approved
  archmage: art('archmage'),
  dragonlord: art('dragonlord', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 5 }),
  eclipse: art('eclipse', { attackCell: 768, attackFrames: 24, attackFps: 16.7, strikeFrame: 10 }),
  eternal: art('eternal'),
  colossus: art('colossus', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 5 }),
  railgun: art('railgun', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 4 }),
  nanite: art('nanite'),
  // Wide strips below are body + FX on 768 px cells (Codex's `_attack_review` composites); 80 ms frames unless noted.
  ironclad: art('ironclad', { attackCell: 768 }), // 8 frames at 16 fps
  artillery: art('artillery', { attackCell: 768, attackFrames: 24, attackFps: 25, strikeFrame: 9 }), // fast slingshot snap, release on 9
  qubit: art('qubit', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 5 }),
  aegis: art('aegis', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 5 }),
  skyfall: art('skyfall', { attackCell: 768, attackFrames: 16, attackFps: 12.5, strikeFrame: 6 }),
  horizon: art('horizon', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 5 }),
  juggernaut: art('juggernaut', { attackCell: 768, attackFrames: 16, attackFps: 12.5, strikeFrame: 6 }),
  photon: art('photon', { attackCell: 768, attackFrames: 12, attackFps: 12.5, strikeFrame: 5 }),
  chronos: art('chronos', { attackCell: 768, attackFrames: 20, attackFps: 12.5, strikeFrame: 8 }),
  atlas: art('atlas', { attackCell: 768, attackFrames: 24, attackFps: 16.7, strikeFrame: 10, idleFrames: 32 }), // 60 ms; 4 s rotating idle
  supernova: art('supernova', { attackCell: 768, attackFrames: 12, attackFps: 14.3, strikeFrame: 5 }), // 70 ms
  omega: art('omega', { attackCell: 768, attackFrames: 16, attackFps: 15.4, strikeFrame: 6 }), // 65 ms
};
